import React, {useEffect, useMemo, useRef, useState} from "react";
import {Card, CardBody, UncontrolledTooltip} from "reactstrap";
import Table, {IColumnProps} from "../../components/organisms/Table/table";
import {useStores} from "../../stores/useStores";
import {observer} from "mobx-react-lite";
import HeaderProgressBar from "../../components/atoms/headerProgressBar/headerProgressBar";
import MetricsHeader from "./components/metricsHeader/metricsHeader";
import "./metricsPage.sass";
import MetricsSearchOptions from "./components/metricsSearchOptions/metricsSearchOptions";
import {SortOrder} from "../../shared/enums/sortOrder";
import Icon from "../../components/atoms/common/icon/icon";
import {IconSource} from "../../shared/enums/iconSource";
import {
  EventMetricsColumn,
  EventsMetricsRow
} from "../../utils/metrics/eventMetrics";
import {useAsync} from "react-async";
import {ToastMessageStatus} from "../../components/atoms/toastMessage/toastMessage";
import Tabs, {Tab} from "../../components/molecules/tabs/tabs";
import {useLocation} from "react-router";
import {MetricsRow} from "../../utils/metrics/metricTypes";

const MetricsPage: React.FC = () => {
  const {
    domainStore: {
      metricsDomainStore: {
        loadData,
        metricsColumns,
        computedMetrics,
        selectedTab,
        setSelectedTab
      }
    },
    uiStore: {toastMessageStore}
  } = useStores();

  const [sortOrder, setSortOrder] = useState(SortOrder.Ascending);
  const [eventsSortColumn, setEventsSortColumn] = useState<string>("village");
  const [noticeSortColumn, setNoticeSortColumn] = useState<string>("village");
  const setSortColumn =
    selectedTab === "Events" ? setEventsSortColumn : setNoticeSortColumn;
  const sortColumn =
    selectedTab === "Events" ? eventsSortColumn : noticeSortColumn;

  // Sticky headers are fun! In this page, we are having only the TH row be sticky, and it needs to "skip" a bit of the page.
  // To make it work, we use CSS custom properties to store the height of the sticky section.
  // Then in the SASS files, we use that custom property to apply the offset for the header row
  const stickyRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const stickyElement = stickyRef.current;
    const pageElement = pageRef.current;
    if (!stickyElement || !pageElement) return;

    const updateStickyTop = () => {
      const rect = stickyElement.getBoundingClientRect();
      pageElement.style.setProperty("--sticky-top", `${rect.height}px`);
    };

    updateStickyTop();

    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(updateStickyTop);
    resizeObserver.observe(stickyElement);

    return () => resizeObserver.disconnect();
  }, []);

  const tabQuery = new URLSearchParams(useLocation().search)?.get("tab");
  useEffect(() => {
    if (
      (tabQuery === "Events" || tabQuery === "Updates") &&
      tabQuery !== selectedTab
    ) {
      setSelectedTab(tabQuery);
    }
  }, [tabQuery, setSelectedTab, selectedTab]);

  const tabs: Tab[] = [
    {
      name: "Events",
      content: <MetricsSearchOptions selectedTab={"Events"} />,
      onClick: () => setSelectedTab("Events"),
      testId: "eventDetailsTab"
    },
    {
      name: "Updates",
      content: <MetricsSearchOptions selectedTab={"Updates"} />,
      onClick: () => setSelectedTab("Updates"),
      testId: "updateDetailsTab"
    }
  ];

  const {isLoading, run} = useAsync({
    deferFn: loadData,
    onReject: () => {
      toastMessageStore.displayToastMessage(
        "Failed to load events",
        ToastMessageStatus.Danger
      );
    }
  });

  const handleSortClick = (column: EventMetricsColumn) => {
    if (sortColumn === column.dataField) {
      setSortOrder(
        sortOrder === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending
      );
    } else {
      // default sort order for village is ascending
      // everything else is descending
      setSortOrder(
        column.dataField === "village"
          ? SortOrder.Ascending
          : SortOrder.Descending
      );
      setSortColumn(column.dataField);
    }
  };

  const sortedMetrics = useMemo(() => {
    return computedMetrics.sort((a, b) => {
      // totals row is always sorted to the top
      if (a.isTotalsRow || b.isTotalsRow) {
        return a.isTotalsRow ? -1 : 1;
      }

      const column = metricsColumns.find((c) => c.dataField === sortColumn)!;
      const aVal = column.rawValue(a);
      const bVal = column.rawValue(b);
      if (aVal < bVal) {
        return sortOrder === SortOrder.Ascending ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortOrder === SortOrder.Ascending ? 1 : -1;
      }
      return 0;
    });
  }, [sortOrder, sortColumn, computedMetrics, metricsColumns]);

  const headerLabelFormatter = (column: EventMetricsColumn) => {
    const isColumnSorting = sortColumn === column.dataField;
    const isSortAscending = sortOrder === SortOrder.Ascending;

    return (
      <div onClick={() => handleSortClick(column)} className={"header-row"}>
        {column.headerLabel}
        {column.sort && (
          <div>
            {column.tooltip && (
              <>
                <Icon source={IconSource.help} id={column.dataField} />
                <UncontrolledTooltip
                  modifiers={{
                    preventOverflow: {boundariesElement: "window"}
                  }}
                  fade={false}
                  className="event-tooltip"
                  target={column.dataField}
                >
                  {column.tooltip}
                </UncontrolledTooltip>
              </>
            )}

            {!isColumnSorting && column.sort && (
              <Icon source={IconSource.toggleBoth} />
            )}

            {isColumnSorting && isSortAscending && (
              <Icon source={IconSource.toggleUp} />
            )}
            {isColumnSorting && !isSortAscending && (
              <Icon source={IconSource.toggleDown} />
            )}
          </div>
        )}
      </div>
    );
  };

  const columns = metricsColumns.map(
    (metric) =>
      ({
        ...metric,
        classes:
          metric.dataField === "village" ? "village-column" : undefined,
        headerFormatter: (row) => {
          const col = metricsColumns.find(
            (c) => c.dataField === row.dataField
          )!;
          return headerLabelFormatter(col);
        }
      } as IColumnProps<EventsMetricsRow>)
  );

  const rowClassFormatter = (row: EventsMetricsRow) =>
    row.isTotalsRow ? "totals-row" : "";

  return (
    <div className={"metrics"} ref={pageRef}>
      <Card>
        <div className={"sticky-top"} ref={stickyRef}>
          <MetricsHeader loadData={run} loading={isLoading} />
        </div>
        <Tabs tabs={tabs} />

        <CardBody>
          <HeaderProgressBar progressBarLoading={isLoading} />
          <div className="table-responsive">
            <Table
              columns={columns}
              data={sortedMetrics}
              keyFormatter={(grp: MetricsRow) => grp.rowKey}
              rowClassFormatter={rowClassFormatter}
              onSort={() => {}}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
export default observer(MetricsPage);
